import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { useMember } from "../context/MemberContext";
import { useOtpGate } from "./OtpGate";
import { ForgotPasswordDialog } from "./ForgotPasswordDialog";
import { CITIES } from "../mock/mockData";
import { districtsOf } from "../lib/districts";
import { formatPhone } from "../lib/phone";
import { Heart, User, Phone, Lock, Loader2, LogIn, UserPlus, MapPin } from "lucide-react";

const MemberDialog = ({ open, onOpenChange }) => {
  const { login, register } = useMember();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const isLogin = mode === "login";

  const { gate, dialog: otpDialog } = useOtpGate("member");
  const [forgotOpen, setForgotOpen] = useState(false);
  const reset = () => { setName(""); setCity(""); setDistrict(""); setPhone(""); setPassword(""); };

  const submit = async () => {
    if (!phone.trim() || !password || (!isLogin && (!name.trim() || !city || !district))) {
      toast({ title: "Eksik bilgi", description: "Lütfen tüm alanları doldurun." });
      return;
    }
    setBusy(true);
    try {
      if (isLogin) {
        await login(phone.trim(), password);
        toast({ title: "Giriş başarılı", description: "Hoş geldiniz!" });
      } else {
        await gate(phone.trim(), async (otp) => {
          await register({ name: name.trim(), phone: phone.trim(), password, city, district, otp });
          toast({ title: "Üyelik oluşturuldu", description: "Hoş geldiniz!" });
          sessionStorage.setItem("ds_new_member", "1");
          reset();
          onOpenChange(false);
          navigate("/uyelik");
        });
        return;
      }
      reset();
      onOpenChange(false);
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "İşlem başarısız." });
    } finally { setBusy(false); }
  };

  return (
    <>
    {otpDialog}
    <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} purpose="member" initialPhone={phone} />
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="member-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-[#2A2224]">
            <span className="w-9 h-9 rounded-full bg-[#FBEEF0] flex items-center justify-center">
              <Heart className="w-4 h-4 text-[#9E2A3B]" fill="#9E2A3B" />
            </span>
            {isLogin ? "Üye Girişi" : "Üye Ol"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex bg-[#FBF0F2] rounded-xl p-1 mb-2">
          <button onClick={() => setMode("login")} data-testid="member-tab-login"
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${isLogin ? "bg-white text-[#9E2A3B] shadow-sm" : "text-[#7C6E70]"}`}>Giriş Yap</button>
          <button onClick={() => setMode("register")} data-testid="member-tab-register"
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${!isLogin ? "bg-white text-[#9E2A3B] shadow-sm" : "text-[#7C6E70]"}`}>Üye Ol</button>
        </div>

        <div className="space-y-3">
          {!isLogin && (
            <div className="space-y-1.5">
              <Label className="text-[#2A2224]">Ad Soyad</Label>
              <div className="relative">
                <User className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="member-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Adınız Soyadınız" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
            </div>
          )}
          {!isLogin && (
            <div className="space-y-1.5">
              <Label className="text-[#2A2224]">Şehir</Label>
              <div className="relative">
                <MapPin className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2 z-10" />
                <Select value={city} onValueChange={(v) => { setCity(v); setDistrict(""); }}>
                  <SelectTrigger data-testid="member-city-select" className="pl-9 border-[#F0DDE0] focus:ring-[#C15B6B]"><SelectValue placeholder="Şehir seçin" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {!isLogin && (
            <div className="space-y-1.5">
              <Label className="text-[#2A2224]">İlçe</Label>
              <div className="relative">
                <MapPin className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2 z-10" />
                <Select value={district} onValueChange={setDistrict} disabled={!city}>
                  <SelectTrigger data-testid="member-district-select" className="pl-9 border-[#F0DDE0] focus:ring-[#C15B6B]"><SelectValue placeholder={city ? "İlçe seçin" : "Önce şehir seçin"} /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {districtsOf(city).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-[#2A2224]">Telefon</Label>
            <div className="relative">
              <Phone className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="member-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} onBlur={() => setPhone(formatPhone(phone))} inputMode="tel" placeholder="05XX XXX XX XX" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#2A2224]">Şifre</Label>
            <div className="relative">
              <Lock className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="member-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="••••••••" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
            {isLogin && (
              <button type="button" onClick={() => setForgotOpen(true)} data-testid="member-forgot-link" className="text-xs text-[#9E2A3B] hover:underline">Şifremi unuttum</button>
            )}
          </div>
          <button onClick={submit} disabled={busy} data-testid="member-submit-button"
            className="w-full h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium flex items-center justify-center gap-2 hover:shadow-md transition-all disabled:opacity-70">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : isLogin ? <LogIn className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            {isLogin ? "Giriş Yap" : "Üye Ol"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default MemberDialog;
